<template>
  <div class="asc-fleet-my-fleet-grid">
    <asc-container>
      <template slot="header">
        <asc-grid-wrapper-header>
          <template v-if="!loading">
            <v-text-field
              class="asc-fleet-my-fleet-grid--search-text"
              prepend-inner-icon="mdi-magnify"
              dense
              hide-details
              clearable
              placeholder="Search for a tail or platform"
              @input="debounceInput"
            ></v-text-field>
          </template>
        </asc-grid-wrapper-header>
      </template>
      <template v-if="loading">
        <asc-spinner-wrapper loading></asc-spinner-wrapper>
      </template>
      <template v-if="!loading">
        <div class="asc-fleet-my-fleet-grid--content">
          <div class="asc-fleet-my-fleet-grid--left-column">
            <asc-fleet-sort-card
              :active="sortIndex === 0"
              :count="getTailsWithIssuesCount"
              :sort-index="0"
              title="Tails With Open MX Recs"
              :on-click="handleSortClick"
            ></asc-fleet-sort-card>
            <asc-fleet-sort-card
              :active="sortIndex === 1"
              :count="getTailsWithFdeCount"
              :sort-index="1"
              title="Tails with Flight Deck Effects"
              :on-click="handleSortClick"
            ></asc-fleet-sort-card>
            <asc-fleet-sort-card
              :active="sortIndex === 2"
              :count="getTailsWithConnectionErrorsCount"
              :sort-index="2"
              title="Tails with Connection Errors"
              :on-click="handleSortClick"
            ></asc-fleet-sort-card>
          </div>
          <div class="asc-fleet-my-fleet-grid--cards">
            <template v-if="!isEmpty">
              <asc-fleet-tail-cardset
                :items="getSortedItems"
                :is-issue-investigator="isIssueInvestigator"
                :is-issue-reader="isIssueReader"
                :is-issue-updater="isIssueUpdater"
                :on-open-maintenance="onOpenMaintenance"
                :on-open-fde="onOpenFde"
                :on-open-connectivity="onOpenConnectivity"
              ></asc-fleet-tail-cardset>
            </template>
            <asc-no-data
              v-if="isEmpty"
              :override-text="getNoDataText"
            ></asc-no-data>
          </div>
        </div>
      </template>
    </asc-container>
  </div>
</template>

<script>
import AscContainer from '../atoms/AscContainer.vue'
import AscSpinnerWrapper from '../atoms/AscSpinnerWrapper.vue'
import AscNoData from '../atoms/AscNoData.vue'

import AscGridWrapperHeader from '../molecules/AscGridWrapperHeader.vue'
import AscFleetSortCard from '../molecules/AscFleetSortCard.vue'
import AscFleetTailCardset from '../molecules/AscFleetTailCardset.vue'

import { debounceShortTime } from '@/utilities/appConsts'
import _debounce from 'lodash/debounce'

export default {
  name: 'AscFleetMyFleetGrid',

  components: {
    AscContainer,
    AscGridWrapperHeader,
    AscFleetSortCard,
    AscFleetTailCardset,
    AscSpinnerWrapper,
    AscNoData,
  },

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isIssueInvestigator: {
      type: Boolean,
      default: false,
    },
    isIssueReader: {
      type: Boolean,
      default: false,
    },
    isIssueUpdater: {
      type: Boolean,
      default: false,
    },
    onOpenMaintenance: {
      type: Function,
      default: () => {},
    },
    onOpenFde: {
      type: Function,
      default: () => {},
    },
    onOpenConnectivity: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      title: 'My Fleet',
      searchText: '',
      sortIndex: 0,
    }
  },

  computed: {
    getNoDataText() {
      return 'There are no tails that match your search criteria'
    },

    getSortedItems() {
      let sortedItems = []
      switch (this.sortIndex) {
        case 0:
          sortedItems = this.getTailsSortedByIssuesCount()
          break
        case 1:
          sortedItems = this.getTailsSortedByFdeCount()
          break
        case 2:
          sortedItems = this.getTailsSortedByConnectionErrors()
          break
        default:
          break
      }

      return sortedItems
    },

    isEmpty() {
      return this.getSortedItems.length === 0
    },

    getTailsWithIssues() {
      return this.getFilteredItems().filter(item => item.issuesCount > 0)
    },

    getTailsWithIssuesCount() {
      return this.getTailsWithIssues.length
    },

    getTailsWithFdes() {
      return this.getFilteredItems().filter(item => item.fdeCount > 0)
    },

    getTailsWithFdeCount() {
      return this.getTailsWithFdes.length
    },

    getTailsWithConnectionErrors() {
      return this.getFilteredItems().filter(item => item.daysOffline >= 3)
    },

    getTailsWithConnectionErrorsCount() {
      return this.getTailsWithConnectionErrors.length
    },
  },

  watch: {
    items(newVal) {
      this.searchText = ''
      this.items = newVal
    },

    searchText(newVal) {
      this.searchText = newVal || ''
    },
  },

  methods: {
    debounceInput: _debounce(function (e) {
      this.searchText = e
    }, debounceShortTime),

    handleSortClick(index) {
      this.sortIndex = index
    },

    getSearchText() {
      return this.searchText || ''
    },

    getFilteredItems() {
      let filteredItems = []
      if (this.searchText === '') {
        filteredItems = this.items.filter(item => item.issuesCount > 0)
      }

      filteredItems = this.items.filter(item =>
        item.searchText.includes(this.searchText.toLowerCase()),
      )

      return filteredItems
    },

    getTailsSortedByIssuesCount() {
      return this.getFilteredItems().sort(
        (x, y) => y.issuesCount - x.issuesCount || x.name.localeCompare(y.name),
      )
    },

    getTailsSortedByFdeCount() {
      return this.getFilteredItems().sort(
        (x, y) => y.fdeCount - x.fdeCount || x.name.localeCompare(y.name),
      )
    },

    getTailsSortedByConnectionErrors() {
      return this.getFilteredItems().sort(
        (x, y) => y.daysOffline - x.daysOffline || x.name.localeCompare(y.name),
      )
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-my-fleet-grid {
  &--search-text {
    width: 250px;
  }

  &--content {
    display: flex;
    margin-top: 32px;
  }

  &--cards {
    margin-top: -8px;
    margin-left: 16px;
    width: 100%;

    .asc-no-data--container {
      height: 300px;
    }
  }
}
</style>
